import { css } from '@emotion/react'
import { s3URL } from '@mathflat/shared/@common/utils/s3'
import clsx from 'clsx'
import { runInAction } from 'mobx'
import { observer } from 'mobx-react'
import type { FC } from 'react'
import { useCallback, useEffect, useRef } from 'react'
import { isTablet } from 'react-device-detect'
import { Rnd, type RndDragEvent } from 'react-rnd'

import type HandwrittenNoteService from './service/HandwrittenNote.service'
import type HandwrittenNoteTextItem from './service/HandwrittenNoteTextItem'

interface Props {
  item: HandwrittenNoteTextItem
  service: HandwrittenNoteService
  activeTextBoxId: number | null
  setActiveTextBoxId: (id: number | null) => void
}

export const MIN_TEXT_BOX_WIDTH = 288
export const MIN_TEXT_BOX_HEIGHT = 84

const HandwrittenNoteTextBox: FC<Props> = ({
  item,
  service,
  activeTextBoxId,
  setActiveTextBoxId,
}) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  const disabledTextBox =
    (!service.controllerService.isHiddenNote && !service.controllerService.isWritingMode) ||
    service.controllerService.activeTool !== 'text'

  const adjustTextBoxSize = () => {
    if (!textareaRef.current) {
      return
    }
    const textarea = textareaRef.current
    const originalHeight = textarea.style.height
    textarea.style.height = 'auto'
    const scrollHeight = textarea.scrollHeight
    textarea.style.height = originalHeight
    const contentHeight = Math.max(MIN_TEXT_BOX_HEIGHT, scrollHeight)
    const currentWidth = item.width || MIN_TEXT_BOX_WIDTH

    runInAction(() => {
      item.setTextboxSize(currentWidth, contentHeight)
    })
  }

  useEffect(() => {
    if (activeTextBoxId === item.id && textareaRef.current) {
      requestAnimationFrame(() => {
        textareaRef.current?.focus()
      })
    }
  }, [activeTextBoxId, item.id])

  useEffect(() => {
    adjustTextBoxSize()
  }, [item.text, item.fontSize, adjustTextBoxSize])

  useEffect(() => {
    // 태블릿에서 키보드가 올라오면 하단 영역이 가려질 수 있어,
    // 생성 위치가 화면 높이의 50% 이하일 경우 텍스트박스를 위쪽에 생성한다.
    if (isTablet) {
      const rndElement = textareaRef.current?.parentElement?.parentElement
      if (!rndElement) {
        return
      }
      const parentElement = rndElement.parentElement
      if (!parentElement) {
        return
      }
      const HALF_OF_TEXTAREA_MIN_WIDTH = 144
      const TABLE_TOP_MARGIN = 10
      const parentHeight = parentElement.clientHeight
      const parentWidth = parentElement.clientWidth
      const halfWidth = parentWidth / 2
      const halfHeight = parentHeight / 2
      if (item.position.y > halfHeight) {
        runInAction(() => {
          item.setPosition({ x: halfWidth - HALF_OF_TEXTAREA_MIN_WIDTH, y: TABLE_TOP_MARGIN })
        })
      }
    }
  }, [item])

  const handleDragStop = (_e: RndDragEvent, data: { x: number; y: number }) => {
    if (disabledTextBox) {
      return
    }
    item.setPosition({ x: data.x, y: data.y })
    service.trackAndSave()
  }

  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    item.setText(e.target.value)
    adjustTextBoxSize()
    service.trackAndSave()
  }

  const handleTextAreaBlur = (e: React.FocusEvent<HTMLTextAreaElement>) => {
    if (e.relatedTarget?.closest('.sub-toolbar') || e.relatedTarget?.closest('.ui-mode-toggle')) {
      return
    }

    setActiveTextBoxId(null)
    if (item.text === '') {
      item.setIsDeleted(true)
    }
    service.trackAndSave()
  }

  // ios 대응
  const handleTextAreaTouchStart = () => {
    if (disabledTextBox) {
      return
    }
    if (textareaRef.current) {
      textareaRef.current.focus()
    }
    setActiveTextBoxId(item.id)
  }

  const handleResizeStop = (
    _e: RndDragEvent,
    ref: HTMLElement,
    position: { x: number; y: number },
  ) => {
    item.setTextboxSize(parseInt(ref.style.width), parseInt(ref.style.height))
    item.setPosition({ x: position.x, y: position.y })
    service.trackAndSave()
  }

  const handleDelete = useCallback(
    (e: React.MouseEvent | React.TouchEvent) => {
      e.stopPropagation()
      setActiveTextBoxId(null)
      item.setIsDeleted(true)
      service.noteData.checkModified()
    },
    [item, service.noteData],
  )

  return (
    <Rnd
      key={`textbox-${item.id}-${activeTextBoxId === item.id ? 'active' : 'inactive'}`}
      css={S.Container}
      position={{ x: item.position.x, y: item.position.y }}
      className={clsx(activeTextBoxId === item.id && 'active')}
      onDragStop={handleDragStop}
      bounds="parent"
      disabled={disabledTextBox}
      size={{
        width: item.width || MIN_TEXT_BOX_WIDTH,
        height: item.height || MIN_TEXT_BOX_HEIGHT,
      }}
      minWidth={MIN_TEXT_BOX_WIDTH}
      minHeight={MIN_TEXT_BOX_HEIGHT}
      enableResizing={
        activeTextBoxId === item.id
          ? {
              top: true,
              right: true,
              bottom: true,
              left: true,
              topRight: false,
              bottomRight: false,
              bottomLeft: false,
              topLeft: false,
            }
          : false
      }
      resizeHandleComponent={
        activeTextBoxId === item.id
          ? {
              top: <ResizeHandle position="top" />,
              bottom: <ResizeHandle position="bottom" />,
              left: <ResizeHandle position="left" />,
              right: <ResizeHandle position="right" />,
            }
          : undefined
      }
      onResizeStop={(e, _direction, ref, _delta, position) => handleResizeStop(e, ref, position)}
    >
      <textarea
        css={S.Textarea}
        className="text-area"
        placeholder={`여기에 내용을 입력하고
드래그 하여 자유롭게 옮겨보세요.`}
        data-textbox="true"
        value={item.text}
        ref={textareaRef}
        onChange={handleTextChange}
        onFocus={() => setActiveTextBoxId(item.id)}
        onTouchStart={handleTextAreaTouchStart}
        disabled={disabledTextBox}
        onBlur={handleTextAreaBlur}
        style={{
          fontSize: `${item.fontSize}px`,
          color: item.color,
          cursor: disabledTextBox ? 'default' : 'move',
          pointerEvents: 'auto',
        }}
      />
      {activeTextBoxId === item.id && (
        <button
          className="remove-button"
          onClick={handleDelete}
          onTouchStart={handleDelete}
          onMouseDown={(e) => {
            e.preventDefault()
          }}
        >
          <img
            src={s3URL.mathflatAssetsBucket.graphic('graphic_close_circle.svg')}
            width={24}
            height={24}
            alt="delete"
          />
        </button>
      )}
    </Rnd>
  )
}

export default observer(HandwrittenNoteTextBox)

const ResizeHandle = ({ position }: { position: string }) => {
  return (
    <div
      css={S.ResizeHandle}
      className={`resize-handle resize-${position}`}
      onMouseDown={(e) => {
        e.preventDefault()
      }}
      style={{
        pointerEvents: 'auto',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: position === 'left' || position === 'right' ? '10px' : '100%',
        height: position === 'top' || position === 'bottom' ? '10px' : '100%',
      }}
    >
      {position === 'top' || position === 'bottom' ? (
        <img
          src={s3URL.mathflatAssetsBucket.graphic('graphic_resize_vertical.svg')}
          width={16}
          height={8}
          alt="resize vertical"
        />
      ) : (
        <img
          src={s3URL.mathflatAssetsBucket.graphic('graphic_resize_horizontal.svg')}
          width={8}
          height={16}
          alt="resize horizontal"
        />
      )}
    </div>
  )
}

const S = {
  Container: css`
    z-index: 100;
    position: absolute;
    top: 0;
    left: 0;
    min-height: ${MIN_TEXT_BOX_HEIGHT}px;
    min-width: ${MIN_TEXT_BOX_WIDTH}px;
    height: 100%;
    width: 100%;
    border: 2px solid transparent;
    background-color: transparent;

    &.active {
      border-radius: var(--Radius-100);
      border: 2px solid var(--Colors-Blue-500);
    }

    .remove-button {
      position: absolute;
      top: -10px;
      right: -10px;
      z-index: 101;
    }
  `,
  ResizeHandle: css`
    pointer-events: 'auto';
    display: flex;
    align-items: center;
    justify-content: center;
    &.resize-handle {
      position: absolute;
    }

    &.resize-top {
      top: 0;
      left: 50%;
      transform: translateX(-50%);
    }

    &.resize-bottom {
      bottom: 0;
      left: 50%;
      transform: translateX(-50%);
    }

    &.resize-left {
      left: 0;
      top: 50%;
      transform: translateY(-50%);
    }

    &.resize-right {
      right: 0;
      top: 50%;
      transform: translateY(-50%);
    }
  `,
  Textarea: css`
    width: 100%;
    height: 100%;
    background-color: transparent;
    resize: none;
    pointer-events: auto;
    padding: 18px 24px;
    border: none;
    white-space: pre-wrap;
    word-wrap: break-word;
    overflow: hidden;

    &:disabled {
      opacity: 1;
    }

    &::placeholder {
      color: rgba(0, 0, 0, 0.3);
    }

    &:read-only {
      cursor: move;
    }
  `,
}
